import React, {ChangeEventHandler, FunctionComponent, MouseEventHandler, useContext, useState} from "react";
import {FirebaseContext} from "../../firebase/FirebaseProvider";
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, FormControl, InputLabel, List, ListItem, ListItemText,
    MenuItem, Select, SelectChangeEvent, TextField
} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import OrderableData from "./OrderableData";
import GroupableData from "./GroupableData";

interface ISelectDataProps {
    admin: boolean
    project: string
    constant: string
    constraint: { data: {values: {[value: string]: string}}}
    id: string
}
function SelectData({admin, constant, constraint, id, project}: ISelectDataProps) {
    const [addValue, setAddValue] = useState("");
    const {remove, set} = useContext(FirebaseContext);
    const valuesPath = `constants/${project}/${constant}/constraints/${id}/data/values`;

    const handleAddClick = () => {
        set(`${valuesPath}/${addValue}`, addValue).then(() => setAddValue(""));
    }
    const handleDeleteClick: MouseEventHandler<HTMLButtonElement> = ({currentTarget}) => 
        remove(`${valuesPath}/${currentTarget.dataset.value}`);
    const handleAddValueChange: ChangeEventHandler<HTMLInputElement> = ({target}) =>
        setAddValue(target.value);
    const updateConstraintValue = (key: string, value: string) => set(`${valuesPath}/${key}`, value);

    return (
        <Box>
            <Box display="flex" m={1}>
                <TextField disabled={!admin} label="Value" value={addValue} size="small" fullWidth
                           onChange={handleAddValueChange}/>
                <Button disabled={
                    addValue === "" || (constraint.data?.values && constraint.data.values[addValue] !== undefined)
                } onClick={handleAddClick} variant="contained" sx={{marginLeft: 1}}>
                    Add
                </Button>
            </Box>
            <List>
                {Object.keys(constraint.data?.values || {}).map(value => 
                    <ListItem key={value}>
                        <TextField disabled={!admin} defaultValue={constraint.data.values[value]} fullWidth
                                   label={`Display for ${value}`} onBlur={({target}) =>
                                       updateConstraintValue(value, target.value)} size="small"/>
                        <Button disabled={!admin} data-value={value} onClick={handleDeleteClick} variant="contained"
                                sx={{marginLeft: 1}}>
                            Delete
                        </Button>
                    </ListItem>
                )}
            </List>
        </Box>
    );
}

const descriptions: {[type: string]: string} = {
    default: "Unknown constraint type",
    unique: "Unique constraint, only one tag of this type permitted",
    select: "Value of tag selected from specific set",
    orderable: "Value of tag is associated with an order",
    groupable: "Constant appears in group list"
};
const dataComponents: {[type: string]: FunctionComponent<any>} = {
    default: () => <></>,
    select: SelectData,
    orderable: OrderableData,
    groupable: GroupableData,
}

export interface IConstraint {
    type: string
    data: any
}
interface IProps {
    admin: boolean
    constant: string
    constraint: IConstraint
    expanded: string
    id: string
    project: string
    setExpanded: (key: string) => void
}

export default function Constraint({admin, constant, constraint, expanded, id, project, setExpanded}: IProps) {
    const {remove, set} = useContext(FirebaseContext);
    const handleTypeChange = ({target}: SelectChangeEvent) =>
        set(`constants/${project}/${constant}/constraints/${id}/type`, target.value);
    const handleDeleteClick = () =>
        remove(`constants/${project}/${constant}/constraints/${id}`);
    const dataComponent = dataComponents[constraint.type] || dataComponents.default;

    return (
        <Box m={1}>
            <Accordion expanded={expanded === id} onChange={() => setExpanded(expanded === id ? "" : id)}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <ListItem>
                        <ListItemText primary={descriptions[constraint.type] || descriptions.default}/>
                    </ListItem>
                </AccordionSummary>
                <AccordionDetails>
                    <Box display="flex">
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select disabled={!admin} label="Type" value={constraint.type} onChange={handleTypeChange}
                                    size="small">
                                <MenuItem value="select">Select</MenuItem>
                                <MenuItem value="unique">Unique</MenuItem>
                                <MenuItem value="orderable">Orderable</MenuItem>
                                <MenuItem value="groupable">Groupable</MenuItem>
                            </Select>
                        </FormControl>
                        <Button disabled={!admin} onClick={handleDeleteClick} variant="contained" sx={{marginLeft: 1}}>
                            Delete
                        </Button>
                    </Box>
                    {React.createElement(dataComponent, {admin, project, constant, constraint, id})}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
